import { Location } from 'history';
import { BlogPostFormData } from 'mk2/apps/blogs/components/BlogPostCreateForm';
import { BoxedHashtags } from 'mk2/apps/blogs/components/HashtagsBox';
import { PageRef } from 'mk2/apps/forum/schemas';
import { GroupCoverFormData } from 'mk2/apps/groups/components/GroupCoverForm';
import { PhotosUploadConfig } from 'mk2/containers/PhotosUpload/PhotosUpload';
import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { NormalizedResponse } from 'mk2/helpers/api';
import { Action } from 'redux';
import { OrderBy, ScrollToPosition } from './GroupFeedPage';

///////////////////////////////////////////////////////////////////
// Progress broadcast

export interface GroupFeedFetchAction extends Action {
    readonly groupId: number;
    readonly pageRef: PageRef;
    readonly orderBy: OrderBy;
    readonly query: string;
}

/////////////////////////////////////////////////////////////////////
// Group feed load
export const GROUP_FEED_FETCH_TRIGGER = 'GROUP_FEED_FETCH_TRIGGER';
export interface GroupFeedFetchTriggerAction extends GroupFeedFetchAction {
    readonly type: typeof GROUP_FEED_FETCH_TRIGGER;
    readonly currentUrl: Location;
    readonly numPostsBeforeLoad: number;
}
export const GROUP_FEED_FETCH_REQUEST = 'GROUP_FEED_FETCH_REQUEST';
export interface GroupFeedFetchRequestAction extends GroupFeedFetchAction, APIRequestAction {
    readonly type: typeof GROUP_FEED_FETCH_REQUEST;
}
export interface GroupFeedFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        complaints: number[];
        group: number;
        posts: number[];
        moderators: number[];
    };
}
export const GROUP_FEED_FETCH_SUCCESS = 'GROUP_FEED_FETCH_SUCCESS';
export interface GroupFeedFetchSuccessAction extends GroupFeedFetchAction, APISuccessAction<GroupFeedFetchNormalizedResponse> {
    readonly type: typeof GROUP_FEED_FETCH_SUCCESS;
    readonly page: number;
    readonly numberOfPages: number;
    readonly count: number;
    readonly memberships: number[];
    readonly askedToJoin: number[];
    readonly favorites: number[];
    readonly isBlocked: boolean;
    readonly isSubscribed: boolean;
    readonly hasComplaints: boolean;
    readonly hashtags: BoxedHashtags;
}
export const GROUP_FEED_FETCH_FAILURE = 'GROUP_FEED_FETCH_FAILURE';
export interface GroupFeedFetchFailureAction extends GroupFeedFetchAction, APIFailureAction {
    readonly type: typeof GROUP_FEED_FETCH_FAILURE;
}

export const groupFeedFetchTrigger =
    (groupId: number, pageRef: PageRef, orderBy: OrderBy, query: string, currentUrl: Location, numCommentsBeforeLoad: number = 0): GroupFeedFetchTriggerAction => ({
        type: GROUP_FEED_FETCH_TRIGGER, groupId, pageRef, orderBy, query, currentUrl, numPostsBeforeLoad: numCommentsBeforeLoad,
    });
export const groupFeedFetchApi = {
    request: (groupId: number, pageRef: PageRef, orderBy: OrderBy, query: string): GroupFeedFetchRequestAction => ({
        type: GROUP_FEED_FETCH_REQUEST, groupId, pageRef, orderBy, query,
    }),
    success: (
        groupId: number, pageRef: PageRef, orderBy: OrderBy, query: string,
        page: number, numberOfPages: number, count: number,
        response: GroupFeedFetchNormalizedResponse, memberships: number[], askedToJoin: number[], favorites: number[],
        isBlocked: boolean, isSubscribed: boolean,
        hasComplaints: boolean, hashtags: BoxedHashtags,
    ): GroupFeedFetchSuccessAction => ({
        type: GROUP_FEED_FETCH_SUCCESS, groupId, pageRef, orderBy, query, page, numberOfPages, count,
        response, memberships, askedToJoin, favorites, isBlocked, isSubscribed, hasComplaints, hashtags,
    }),
    failure: (groupId: number, pageRef: PageRef, orderBy: OrderBy, query: string, error: any): GroupFeedFetchFailureAction => ({
        type: GROUP_FEED_FETCH_FAILURE, groupId, pageRef, orderBy, query, error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Group feed - scrollto

export const GROUP_FEED_SCROLLTO_TRIGGER = 'GROUP_FEED_SCROLLTO_TRIGGER';
export interface GroupFeedScrollToTriggerAction {
    readonly type: typeof GROUP_FEED_SCROLLTO_TRIGGER;
    readonly scrollto: ScrollToPosition;
}

export const groupFeedScrollToTrigger = (scrollto: ScrollToPosition): GroupFeedScrollToTriggerAction => ({
    type: GROUP_FEED_SCROLLTO_TRIGGER, scrollto,
});

/////////////////////////////////////////////////////////////////////
// Group Cover Upload
export const GROUP_COVER_UPLOAD_TRIGGER = 'GROUP_COVER_UPLOAD_TRIGGER';
export interface GroupCoverUploadTriggerAction {
    readonly type: typeof GROUP_COVER_UPLOAD_TRIGGER;
    readonly groupId: number;
    readonly config: PhotosUploadConfig;
    readonly files: File[];
}
export const GROUP_COVER_UPLOAD_REQUEST = 'GROUP_COVER_UPLOAD_REQUEST';
export interface GroupCoverUploadRequestAction extends APIRequestAction {
    readonly type: typeof GROUP_COVER_UPLOAD_REQUEST;
    readonly groupId: number;
    readonly config: PhotosUploadConfig;
    readonly files: File[];
}
export interface GroupCoverUploadNormalizedResponse extends NormalizedResponse {
    readonly result: number;
}
export const GROUP_COVER_UPLOAD_SUCCESS = 'GROUP_COVER_UPLOAD_SUCCESS';
export interface GroupCoverUploadSuccessAction extends APISuccessAction<GroupCoverUploadNormalizedResponse> {
    readonly type: typeof GROUP_COVER_UPLOAD_SUCCESS;
    readonly groupId: number;
    readonly config: PhotosUploadConfig;
    readonly files: File[];
    readonly response: GroupCoverUploadNormalizedResponse;
}
export const GROUP_COVER_UPLOAD_FAILURE = 'GROUP_COVER_UPLOAD_FAILURE';
export interface GroupCoverUploadFailureAction extends APIFailureAction {
    readonly type: typeof GROUP_COVER_UPLOAD_FAILURE;
    readonly groupId: number;
    readonly config: PhotosUploadConfig;
    readonly files: File[];
}
export const groupCoverUploadTrigger =
    (groupId: number, config: PhotosUploadConfig, files: File[]): GroupCoverUploadTriggerAction => {
        return ({
            type: GROUP_COVER_UPLOAD_TRIGGER, groupId, config, files,
        });
    };
export const groupCoverUploadApi = {
    request: (groupId: number, config: PhotosUploadConfig, files: File[]): GroupCoverUploadRequestAction => ({
        type: GROUP_COVER_UPLOAD_REQUEST, groupId, config, files,
    }),
    success: (groupId: number, config: PhotosUploadConfig, files: File[], response: GroupCoverUploadNormalizedResponse): GroupCoverUploadSuccessAction => ({
        type: GROUP_COVER_UPLOAD_SUCCESS, groupId, config, files, response,
    }),
    failure: (groupId: number, config: PhotosUploadConfig, files: File[], error: any): GroupCoverUploadFailureAction => ({
        type: GROUP_COVER_UPLOAD_FAILURE, groupId, config, files, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Group Cover Remove
export const GROUP_COVER_REMOVE_TRIGGER = 'GROUP_COVER_REMOVE_TRIGGER';
export interface GroupCoverRemoveTriggerAction {
    readonly type: typeof GROUP_COVER_REMOVE_TRIGGER;
    readonly groupId: number;
}
export const GROUP_COVER_REMOVE_REQUEST = 'GROUP_COVER_REMOVE_REQUEST';
export interface GroupCoverRemoveRequestAction extends APIRequestAction {
    readonly type: typeof GROUP_COVER_REMOVE_REQUEST;
    readonly groupId: number;
}
export interface GroupCoverRemoveNormalizedResponse extends NormalizedResponse {
    readonly result: { group: number };
}
export const GROUP_COVER_REMOVE_SUCCESS = 'GROUP_COVER_REMOVE_SUCCESS';
export interface GroupCoverRemoveSuccessAction extends APISuccessAction<GroupCoverRemoveNormalizedResponse> {
    readonly type: typeof GROUP_COVER_REMOVE_SUCCESS;
    readonly groupId: number;
}
export const GROUP_COVER_REMOVE_FAILURE = 'GROUP_COVER_REMOVE_FAILURE';
export interface GroupCoverRemoveFailureAction extends APIFailureAction {
    readonly type: typeof GROUP_COVER_REMOVE_FAILURE;
    readonly groupId: number;
}

export const groupCoverRemoveTrigger =
    (groupId: number): GroupCoverRemoveTriggerAction => ({
        type: GROUP_COVER_REMOVE_TRIGGER, groupId,
    });
export const groupCoverRemoveApi = {
    request: (groupId: number): GroupCoverRemoveRequestAction => ({
        type: GROUP_COVER_REMOVE_REQUEST, groupId,
    }),
    success: (groupId: number, response: GroupCoverRemoveNormalizedResponse): GroupCoverRemoveSuccessAction => ({
        type: GROUP_COVER_REMOVE_SUCCESS, groupId, response,
    }),
    failure: (groupId: number, error: any): GroupCoverRemoveFailureAction => ({
        type: GROUP_COVER_REMOVE_FAILURE, groupId, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Group Cover Submit
export const GROUP_COVER_SUBMIT_TRIGGER = 'GROUP_COVER_SUBMIT_TRIGGER';
export interface GroupCoverSubmitTriggerAction {
    readonly type: typeof GROUP_COVER_SUBMIT_TRIGGER;
    readonly values: GroupCoverFormData;
    readonly groupId: number;
}
export const GROUP_COVER_SUBMIT_REQUEST = 'GROUP_COVER_SUBMIT_REQUEST';
export interface GroupCoverSubmitRequestAction extends APIRequestAction {
    readonly type: typeof GROUP_COVER_SUBMIT_REQUEST;
    readonly values: GroupCoverFormData;
    readonly groupId: number;
}
export interface GroupCoverSubmitNormalizedResponse extends NormalizedResponse {
    readonly result: { group: number };
}
export const GROUP_COVER_SUBMIT_SUCCESS = 'GROUP_COVER_SUBMIT_SUCCESS';
export interface GroupCoverSubmitSuccessAction extends APISuccessAction<GroupCoverSubmitNormalizedResponse> {
    readonly type: typeof GROUP_COVER_SUBMIT_SUCCESS;
    readonly values: GroupCoverFormData;
    readonly groupId: number;
}
export const GROUP_COVER_SUBMIT_FAILURE = 'GROUP_COVER_SUBMIT_FAILURE';
export interface GroupCoverSubmitFailureAction extends APIFailureAction {
    readonly type: typeof GROUP_COVER_SUBMIT_FAILURE;
    readonly values: GroupCoverFormData;
    readonly groupId: number;
}

export const groupCoverSubmitTrigger =
    (values: GroupCoverFormData, groupId: number): GroupCoverSubmitTriggerAction => ({
        type: GROUP_COVER_SUBMIT_TRIGGER, values, groupId,
    });
export const groupCoverSubmitApi = {
    request: (values: GroupCoverFormData, groupId: number): GroupCoverSubmitRequestAction => ({
        type: GROUP_COVER_SUBMIT_REQUEST, values, groupId,
    }),
    success: (values: GroupCoverFormData, groupId: number, response: GroupCoverSubmitNormalizedResponse): GroupCoverSubmitSuccessAction => ({
        type: GROUP_COVER_SUBMIT_SUCCESS, values, groupId, response,
    }),
    failure: (values: GroupCoverFormData, groupId: number, error: any): GroupCoverSubmitFailureAction => ({
        type: GROUP_COVER_SUBMIT_FAILURE, values, groupId, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Group Post Create

export const GROUP_POST_CREATE_TRIGGER = 'GROUP_POST_CREATE_TRIGGER';
export interface GroupPostCreateTriggerAction {
    readonly type: typeof GROUP_POST_CREATE_TRIGGER;
    readonly groupId: number;
    readonly pageRef: PageRef;
    readonly postFd: BlogPostFormData;
}
export const GROUP_POST_CREATE_REQUEST = 'GROUP_POST_CREATE_REQUEST';
export interface GroupPostCreateRequestAction extends APIRequestAction {
    readonly type: typeof GROUP_POST_CREATE_REQUEST;
    readonly groupId: number;
    readonly postFd: BlogPostFormData;
}
export interface GroupPostCreateNormalizedResponse extends NormalizedResponse {
    readonly result: { post: number; };
}
export const GROUP_POST_CREATE_SUCCESS = 'GROUP_POST_CREATE_SUCCESS';
export interface GroupPostCreateSuccessAction extends APISuccessAction<GroupPostCreateNormalizedResponse> {
    readonly type: typeof GROUP_POST_CREATE_SUCCESS;
    readonly groupId: number;
    readonly postFd: BlogPostFormData;
}
export const GROUP_POST_CREATE_FAILURE = 'GROUP_POST_CREATE_FAILURE';
export interface GroupPostCreateFailureAction extends APIFailureAction {
    readonly type: typeof GROUP_POST_CREATE_FAILURE;
    readonly groupId: number;
    readonly postFd: BlogPostFormData;
}

export const groupPostCreateTrigger =
    (groupId: number, pageRef: PageRef, postFd: BlogPostFormData): GroupPostCreateTriggerAction => ({
        type: GROUP_POST_CREATE_TRIGGER, groupId, pageRef, postFd,
    });
export const groupPostCreateApi = {
    request: (groupId: number, postFd: BlogPostFormData): GroupPostCreateRequestAction => ({
        type: GROUP_POST_CREATE_REQUEST, groupId, postFd,
    }),
    success: (groupId: number, postFd: BlogPostFormData, response: GroupPostCreateNormalizedResponse): GroupPostCreateSuccessAction => ({
        type: GROUP_POST_CREATE_SUCCESS, groupId, postFd, response,
    }),
    failure: (groupId: number, postFd: BlogPostFormData, error: any): GroupPostCreateFailureAction => ({
        type: GROUP_POST_CREATE_FAILURE, groupId, postFd, error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Group Delete
export interface GroupDeleteGroupApiAction extends Action {
    readonly groupId: number;
}

export const GROUP_DELETE_GROUP_API_REQUEST = 'GROUP_DELETE_GROUP_API_REQUEST';

export interface GroupDeleteGroupApiRequestAction extends GroupDeleteGroupApiAction, APIRequestAction {
    readonly type: typeof GROUP_DELETE_GROUP_API_REQUEST;
}

export const GROUP_DELETE_GROUP_API_SUCCESS = 'GROUP_DELETE_GROUP_API_SUCCESS';

export interface GroupDeleteNormalizedResponse extends NormalizedResponse {
    readonly result: { group: number; isMember: boolean };
}

export interface GroupDeleteGroupApiSuccessAction extends GroupDeleteGroupApiAction, APISuccessAction<GroupDeleteNormalizedResponse> {
    readonly type: typeof GROUP_DELETE_GROUP_API_SUCCESS;
}

export const GROUP_DELETE_GROUP_API_FAILURE = 'GROUP_DELETE_GROUP_API_FAILURE';

export interface GroupDeleteGroupApiFailureAction extends GroupDeleteGroupApiAction, APIFailureAction {
    readonly type: typeof GROUP_DELETE_GROUP_API_FAILURE;
}

export const groupDeleteGroupApi = {
    request: (groupId: number): GroupDeleteGroupApiRequestAction => ({ type: GROUP_DELETE_GROUP_API_REQUEST, groupId }),
    success: (groupId: number, response: GroupDeleteNormalizedResponse): GroupDeleteGroupApiSuccessAction => ({ type: GROUP_DELETE_GROUP_API_SUCCESS, groupId, response }),
    failure: (groupId: number, error: any): GroupDeleteGroupApiFailureAction => ({ type: GROUP_DELETE_GROUP_API_FAILURE, groupId, error }),
};

export const GROUP_DELETE_GROUP = 'GROUP_DELETE_GROUP';

export interface GroupDeleteGroupAction extends GroupDeleteGroupApiAction {
    readonly type: typeof GROUP_DELETE_GROUP;
}

export const groupDeleteGroup = (groupId: number): GroupDeleteGroupAction => ({ type: GROUP_DELETE_GROUP, groupId });
